import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Button, ButtonGroup, Input } from '@iq/react-components';

import TagSelect from '../../../../TagSelect';
import TypeSelect from '../../../../TypeSelect';

const Filters = ({ filterValue, hideSearch, setFilterValue }) => {
  if (hideSearch) return null;

  return (
    <Input
      className="files-panel-search"
      type="text"
      placeholder="Search..."
      value={filterValue}
      onChange={(event) => setFilterValue(event.target.value)}
    />
  );
};

const TypeTagFilters = ({
  availableTags,
  availableTypes,
  includeTags = [],
  includeTypes = [],
  limitTags,
  setFiletypeFilter,
  setTagFilter,
}) => {
  const [typeValue, setTypeValue] = useState(includeTypes);
  const [tagValue, setTagValue] = useState(includeTags);

  useEffect(() => {
    setFiletypeFilter(() => typeValue || []);
  }, [JSON.stringify(typeValue)]);

  useEffect(() => {
    setTagFilter(() => tagValue || []);
  }, [JSON.stringify(tagValue)]);

  useEffect(() => {
    setTypeValue(() => includeTypes);
  }, [JSON.stringify(includeTypes)]);

  useEffect(() => {
    setTagValue(() => includeTags);
  }, [JSON.stringify(includeTags)]);

  const isFixedTagOption = useCallback(({ value }) => includeTags.includes(value), [includeTags]);

  return (
    <div className="filter-tools-select-wrapper">
      <TypeSelect
        onChange={setTypeValue}
        value={typeValue}
        popout
        availableTypes={availableTypes}
        isDisabled={includeTypes.length > 0}
        menuXPlacement="left"
        limitTypes={includeTypes}
        hideMenu={includeTypes.length > 0}
      />
      <TagSelect
        onChange={setTagValue}
        value={tagValue}
        creatable={false}
        popout
        availableTags={availableTags}
        isDisabled={availableTags.length === 0 || limitTags.fixedTags}
        menuXPlacement="left"
        isOptionDisabled={isFixedTagOption}
        limitTags={limitTags}
        backspaceRemovesValue={(limitTags.includesTogether || []).length === 0}
        hideMenu={limitTags.fixedTags}
        category="files"
      />
    </div>
  );
};

const FilesHeader = ({
  availableTags,
  availableTypes,
  canDelete,
  canDownload,
  canEdit,
  disableSelect,
  fileViewType,
  files = [],
  filterValue,
  format,
  hideFilters,
  hideSearch,
  hideToolbar,
  hideUploadButton,
  includeTags,
  includeTypes,
  isFullScreen,
  layouts,
  limitTags,
  onDeleteSelected,
  onDownloadSelected,
  onEditSelected,
  selectedFiles,
  setDisplayUploadModal,
  setFileViewType,
  setFiletypeFilter,
  setFilterValue,
  setTagFilter,
  toggleSelectAllFiles,
  width,
}) => {
  if (hideToolbar) return null;

  const panelViews = [
    {
      type: 'table',
      icon: 'he-list',
      tooltip: 'View as list',
    },
    {
      type: 'grid',
      icon: 'he-cards',
      tooltip: 'View as thumbnails',
    },
  ];

  const allSelected =
    files && selectedFiles && files.length > 0 && files.length === selectedFiles.length;

  return (
    <div className={`files-panel-header ${isFullScreen ? 'widest' : format}`}>
      <div className="files-panel-toolbar primary">
        <div className="files-panel-toolbar-btn-wrapper">
          {['Grid & list', 'List & grid'].includes(layouts) && (
            <ButtonGroup
              layouts={panelViews}
              activeLayout={fileViewType}
              onClick={setFileViewType}
            />
          )}
          {!hideUploadButton && canEdit && (
            <Button
              activity="primary"
              onClick={() => setDisplayUploadModal(true)}
              tooltip={width < 800 && 'Add files'}
            >
              <div className="button-text">{format === 'narrow' ? 'Add' : 'Add files'}</div>
            </Button>
          )}
        </div>
        <div className="filter-tools">
          <Filters
            filterValue={filterValue}
            hideSearch={hideSearch}
            setFilterValue={setFilterValue}
          />
          {hideFilters ? null : (
            <TypeTagFilters
              availableTags={availableTags}
              availableTypes={availableTypes}
              includeTags={includeTags}
              includeTypes={includeTypes}
              limitTags={limitTags}
              setFiletypeFilter={setFiletypeFilter}
              setTagFilter={setTagFilter}
            />
          )}
        </div>
      </div>
      {(!disableSelect || !hideUploadButton) && (
        <div className="files-panel-toolbar secondary">
          {!disableSelect && (
            <>
              <label className="select-all-label">
                <Icon
                  className={`select-all ${allSelected ? 'checked' : ''}`}
                  icon={`${allSelected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                  size="s"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleSelectAllFiles(files);
                  }}
                />
                <span>Select all</span>
              </label>

              {selectedFiles.length > 0 && (
                <div className="handle-selected-files">
                  {canDownload && (
                    <Button
                      onClick={onDownloadSelected}
                      activity="secondary"
                      icon={<Icon icon="he-download" />}
                      tooltip="Download selected"
                      slim={format === 'narrow' && true}
                    />
                  )}
                  {canDelete && (
                    <Button
                      onClick={onDeleteSelected}
                      activity="secondary"
                      icon={<Icon icon="he-delete" />}
                      tooltip="Delete selected"
                      slim={format === 'narrow' && true}
                    />
                  )}
                  {canEdit && (
                    <Button
                      onClick={onEditSelected}
                      activity="secondary"
                      disabled={selectedFiles.filter((f) => !f.bookmarkData).length === 0}
                      icon={<Icon icon="he-edit" />}
                      tooltip="Edit selected"
                      slim={format === 'narrow' && true}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FilesHeader;
